import { z } from 'zod'

export const EnvironmentTypeSchema = z.enum(['ground', 'water', 'air'])
export type EnvironmentType = z.infer<typeof EnvironmentTypeSchema>

export const StanceTypeSchema = z.enum(['stand', 'crouch', 'swim', 'fly'])
export type StanceType = z.infer<typeof StanceTypeSchema>

export const ActionTypeSchema = z.enum(['idle', 'walk', 'run', 'attack', 'hurt', 'death'])
export type ActionType = z.infer<typeof ActionTypeSchema>

export const CategorySchema = z.enum([
  'INFANTRY',
  'CYBORG',
  'ROBOTIC',
  'AQUATIC',
  'AERIAL',
  'HEAVY',
  'BOSS',
  'STATIC_PROP',
])
export type CharacterCategory = z.infer<typeof CategorySchema>

export const AnimationTrackSchema = z.object({
  sectionIndex: z.number().int().nonnegative(),
  frameCount: z.number().int().positive(),
  fps: z.number().positive(),
  stance: StanceTypeSchema,
  action: ActionTypeSchema,
  description: z.string().optional(),
})
export type AnimationTrack = z.infer<typeof AnimationTrackSchema>

export const TextureClusterSchema = z.object({
  clusterId: z.string(),
  dimension: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  }),
  totalLayers: z.number().int().positive().max(256),
  tracks: z.array(z.string()),
})
export type TextureCluster = z.infer<typeof TextureClusterSchema>

export const CharacterSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  category: CategorySchema,
  defaultEnvironment: EnvironmentTypeSchema,
  allowedEnvironments: z.array(EnvironmentTypeSchema).min(1),
  allowedStances: z.array(StanceTypeSchema).min(1),
  worldSize: z.number().positive(),
  dimension: z.object({
    widthMeters: z.number().positive(),
    heightMeters: z.number().positive(),
    depthMeters: z.number().positive().default(0.2),
  }),
  sourceTag: z.string(),
  numAngles: z.number().int().positive(),
  textureClusters: z.array(TextureClusterSchema).optional(),
  tracks: z.record(z.string(), AnimationTrackSchema),
})
export type CharacterSchemaType = z.infer<typeof CharacterSchema>

export const CharacterManifestSchema = z.record(z.string(), CharacterSchema)
export type CharacterManifestType = z.infer<typeof CharacterManifestSchema>

/**
 * Validates raw JSON data against CharacterManifestSchema.
 */
export function parseCharacterManifest(data: unknown): CharacterManifestType {
  return CharacterManifestSchema.parse(data)
}
